import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	forwardRef,
	inject,
	input,
	OnDestroy,
	signal,
	ViewChild,
	WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { ENVIRONMENT } from '../../../environments/environment.interface';
import { DocumentFile, DocumentFileSubjectType } from '../../api/app-types/document.app';
import { UPLOAD_API } from '../../api/services/upload.api';

interface UploadState {
	file?: File;
	documentFile?: DocumentFile;
	documentId?: string;
	progress: WritableSignal<number>;
	status: WritableSignal<'pending' | 'uploading' | 'completed' | 'error' | 'cancelled'>;
	error?: string;
	uploadSubscription?: Subscription;
	cancelFn?: () => void;
}

@Component({
	selector: 'lvr-document-upload',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatButtonModule, MatIconModule, MatProgressBarModule, TranslocoModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DocumentUploadControl),
			multi: true,
		},
	],
	templateUrl: './document-upload.html',
	styleUrl: './document-upload.scss',
})
export class DocumentUploadControl implements ControlValueAccessor, OnDestroy {
	// Input properties
	public readonly multiple = input<boolean>(false);
	private readonly env = inject(ENVIRONMENT);
	public readonly accept = input<string>(this.env.fileSettings.allowedMimeTypes.join(', '));
	public readonly maxFileSize = input<number | undefined>(undefined);
	public readonly disabled = input<boolean>(false);
	public readonly showIcon = input<boolean>(true);
	public readonly icon = input<string>('add');
	public readonly ownerId = input.required<string | number>();
	public readonly subjectType = input.required<DocumentFileSubjectType>();
	// eslint-disable-next-line @angular-eslint/no-input-rename
	public readonly inputError = input<string>('', { alias: 'hasError' });
	public readonly internalError = signal<string>('');
	public readonly hasError = computed(() => this.internalError() || this.inputError());
	public readonly maxFileCount = input<number | undefined>(undefined);
	public readonly selectionMessage = signal<string>('');
	// Template reference to hidden file input
	@ViewChild('fileInput', { static: true })
	private fileInput!: ElementRef<HTMLInputElement>;

	private readonly uploadApi = inject(UPLOAD_API);

	// Component state
	public readonly uploads = signal<UploadState[]>([]);
	public readonly pendingUploads = computed(() => {
		return this.uploads().filter((upload) => upload.status() === 'pending' || upload.status() === 'uploading');
	});
	public readonly hasFiles = signal<boolean>(false);

	public readonly activeUploadCount = computed(() => {
		return this.uploads().filter(
			(upload) => upload.status() === 'pending' || upload.status() === 'uploading' || upload.status() === 'completed',
		).length;
	});

	public readonly isMaxFileCountReached = computed(() => {
		const max = this.maxFileCount();
		return !!max && this.activeUploadCount() >= max;
	});

	public readonly remainingFileSlots = computed(() => {
		const max = this.maxFileCount();
		if (!max) return this.env.proofSettings.maxFiles;

		return Math.max(0, max - this.activeUploadCount());
	});

	public readonly isUploadDisabled = computed(() => {
		return this.disabled() || this.isMaxFileCountReached();
	});

	// Form control value
	private value: DocumentFile | DocumentFile[] | null = null;

	public currentValue() {
		return Array.isArray(this.value) ? this.value : this.value ? [this.value] : [];
	}

	// ControlValueAccessor callbacks
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onChange = (_value: DocumentFile | DocumentFile[] | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private onTouched = () => {};

	public ngOnDestroy() {
		// Cancel all ongoing uploads and clean up subscriptions
		this.uploads().forEach((upload) => {
			upload.uploadSubscription?.unsubscribe();
			upload.cancelFn?.();
		});
	}

	public writeValue(value: DocumentFile | DocumentFile[] | null) {
		this.value = value;
		const docs = Array.isArray(value) ? value : value ? [value] : [];
		const completed: UploadState[] = docs.map((d) => ({
			documentFile: d,
			documentId: d.id,
			progress: signal(100),
			status: signal('completed'),
		}));
		this.uploads.set(completed);
		this.updateHasFiles();
	}

	public registerOnChange(fn: (value: DocumentFile | DocumentFile[] | null) => void) {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	public setDisabledState(_isDisabled: boolean) {
		// The disabled state is handled via the disabled input
		// This method is called by the form control
	}

	// File selection handling
	public onFileButtonClick() {
		if (this.isUploadDisabled()) return;

		this.fileInput.nativeElement.click();
	}

	public onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		const files = input.files;

		if (!files || files.length === 0) return;

		this.onTouched();
		this.internalError.set('');

		if (this.isMaxFileCountReached()) {
			this.selectionMessage.set('maxFileCountReached');
			input.value = '';
			return;
		}

		// Convert FileList to Array and validate files
		const fileArray = Array.from(files);
		const validFiles = this.validateFiles(fileArray);

		if (validFiles.length > 0) {
			this.startUploads(validFiles);
		}

		// Clear the input so the same file can be selected again
		input.value = '';
	}

	public cancelUpload(uploadIndex: number) {
		const currentUploads = this.uploads();
		const upload = currentUploads[uploadIndex];

		if (upload && upload.status() === 'uploading') {
			upload.status.set('cancelled');
			upload.uploadSubscription?.unsubscribe();
			upload.cancelFn?.();

			// Remove cancelled upload from the list
			this.removeUpload(uploadIndex);
		}
	}

	public removeCompletedFile(index: number) {
		const list = this.uploads();
		const item = list[index];
		if (!item) return;

		// If still uploading, cancel instead
		if (item.status() === 'uploading') {
			this.cancelUpload(index);
			return;
		}

		// Remove locally first
		const prev = [...list];
		this.uploads.set(prev.filter((_, i) => i !== index));
		this.updateFormValue();
		this.updateHasFiles();
		this.clearLimitMessagesIfNeeded();
	}

	public formatFileSize(bytes: number) {
		if (bytes === 0) return '0 Bytes';

		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
	}

	private validateFiles(files: File[]): File[] {
		const maxSize = this.maxFileSize();
		const remainingSlots = this.remainingFileSlots();

		this.selectionMessage.set('');
		this.internalError.set('');

		if (remainingSlots <= 0) {
			this.selectionMessage.set('maxFileCountReached');
			return [];
		}

		const filesWithinLimit = files.slice(0, remainingSlots);

		if (files.length > remainingSlots) {
			this.selectionMessage.set('maxFileCountExceeded');
		}

		return filesWithinLimit.filter((file) => {
			if (maxSize && file.size > maxSize) {
				console.warn(`File ${file.name} exceeds maximum size of ${maxSize} bytes`);
				this.internalError.set('maxFileSize');
				return false;
			}
			return true;
		});
	}

	private clearLimitMessagesIfNeeded() {
		if (!this.isMaxFileCountReached()) {
			this.selectionMessage.set('');
		}
	}

	private startUploads(files: File[]) {
		const newUploads: UploadState[] = files.map((file) => ({
			file,
			progress: signal(0),
			status: signal('pending' as const),
		}));

		// Add new uploads to existing ones (for multiple mode) or replace (for single mode)
		if (this.multiple()) {
			this.uploads.update((current) => [...current, ...newUploads]);
		} else {
			// Cancel any existing uploads in single mode
			this.uploads().forEach((upload) => {
				upload.uploadSubscription?.unsubscribe();
				upload.cancelFn?.();
			});
			this.uploads.set(newUploads);
		}

		// Start uploading each file
		newUploads.forEach((upload) => {
			this.uploadFile(upload);
		});

		this.updateHasFiles();
	}

	private uploadFile(upload: UploadState) {
		upload.status.set('uploading');

		const uploadResult = this.uploadApi.uploadFile(upload.file!, this.subjectType(), this.ownerId());

		upload.cancelFn = uploadResult.cancel;

		upload.uploadSubscription = uploadResult.request.subscribe({
			next: (result) => {
				if (typeof result === 'number') {
					// Progress update
					upload.progress.set(result);
				} else {
					// Upload completed
					upload.status.set('completed');
					upload.progress.set(100);
					upload.documentFile = (result as DocumentFile) || undefined;
					upload.documentId = upload.documentFile?.id;

					this.updateFormValue();
				}
			},
			error: (error) => {
				upload.status.set('error');
				const payload = error?.error;
				const errorText = typeof payload === 'string' ? payload : (payload?.detail ?? payload?.title ?? '');
				if (errorText.includes('Content too large')) {
					upload.error = `Datei ist zu groß. Es sind maximal ${this.maxFileSize()! / 1024 / 1024}MB erlaubt.`;
				} else {
					upload.error = errorText || error?.message || 'Upload failed';
				}
				console.error('Upload error:', error);
			},
		});
	}

	private removeUpload(index: number) {
		this.uploads.update((current) => {
			if (index < 0 || index >= current.length) return current;
			const next = [...current];
			const removed = next[index];
			removed?.uploadSubscription?.unsubscribe();
			removed?.cancelFn?.();
			next.splice(index, 1);
			return next;
		});
		this.updateFormValue();
		this.updateHasFiles();
		this.clearLimitMessagesIfNeeded();
	}

	private updateFormValue() {
		const completedUploads = this.uploads().filter((upload) => upload.status() === 'completed' && upload.documentFile);

		const documentFiles = completedUploads.map((upload) => upload.documentFile!).filter(Boolean);

		if (this.multiple()) {
			this.value = documentFiles;
		} else {
			this.value = documentFiles.length > 0 ? documentFiles[0] : null;
		}

		this.onChange(this.value);
	}

	private updateHasFiles() {
		const hasUploads = this.uploads().length > 0;
		const hasValue = this.multiple() ? Array.isArray(this.value) && this.value.length > 0 : this.value !== null;

		this.hasFiles.set(hasUploads || hasValue);
	}
}
